import { t as uiText } from '../../i18n';
import { getCombinedFontFamily } from '../../utils/fontStacks';
import { appearanceDraftsEqual, type AppearanceDraft } from './appearanceDraft';

const DEFAULT_BUTTON_COLOR = '#cfae91';

export type WidgetPositionPreset =
  | 'center'
  | 'right-center'
  | 'right-bottom'
  | 'left-center'
  | 'left-bottom';

/** ウィジェット設定画面のフローティングボタンと同じ見た目。 */
export interface WidgetLauncherVisual {
  text: string;
  textColor: string;
  fontSize: number;
  fontWeight: number;
  fontFamily: string;
  writingMode: 'horizontal' | 'vertical';
  tcyEnabled: boolean;
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  width: number;
  height: number;
  borderRadius: number;
  paddingY: number;
  paddingX: number;
  buttonType: 'text' | 'image';
  imageUrl: string;
  imageFit: 'contain' | 'cover' | 'fill';
  imageMode: 'styled' | 'image-only';
  showCloseButton: boolean;
  icon: {
    url: string;
    size: number;
    color: string;
    colorEnabled: boolean;
    position: 'before' | 'after';
  } | null;
  closeButton: {
    iconColor: string;
    backgroundColor: string;
    opacity: number;
    borderColor: string;
  };
  positionPreset: string;
  offset: { top: number; right: number; bottom: number; left: number };
  edge: 'content' | 'window';
  widgetPosition: WidgetPositionPreset;
  widgetOffset: { top: number; right: number; bottom: number; left: number };
  widgetWidth: number;
  dimBackground: boolean;
  tooltip: {
    enabled: boolean;
    text: string;
    backgroundColor: string;
    textColor: string;
    textAlign: 'center' | 'left';
    maxWidth: number;
  };
}

const WIDGET_PRESETS: WidgetPositionPreset[] = [
  'center',
  'right-center',
  'right-bottom',
  'left-center',
  'left-bottom',
];

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asWidgetPreset(value: unknown): WidgetPositionPreset {
  return WIDGET_PRESETS.includes(value as WidgetPositionPreset) ? (value as WidgetPositionPreset) : 'center';
}

function asImageFit(value: unknown): 'contain' | 'cover' | 'fill' {
  return value === 'cover' || value === 'fill' || value === 'contain' ? value : 'contain';
}

export function defaultWidgetLauncherVisual(fontFamily?: string): WidgetLauncherVisual {
  return {
    text: uiText('Frequently asked questions'),
    textColor: '#ffffff',
    fontSize: 16,
    fontWeight: 700,
    fontFamily: fontFamily || getCombinedFontFamily('default', 'default'),
    writingMode: 'horizontal',
    tcyEnabled: false,
    backgroundColor: DEFAULT_BUTTON_COLOR,
    borderColor: '#00000000',
    borderWidth: 0,
    width: 220,
    height: 60,
    borderRadius: 30,
    paddingY: 0,
    paddingX: 8,
    buttonType: 'text',
    imageUrl: '',
    imageFit: 'contain',
    imageMode: 'styled',
    showCloseButton: false,
    icon: null,
    closeButton: {
      iconColor: '#ffffff',
      backgroundColor: '#000000',
      opacity: 0.6,
      borderColor: '#ffffff',
    },
    positionPreset: 'right-bottom',
    offset: { top: 0, right: 0, bottom: 0, left: 0 },
    edge: 'content',
    widgetPosition: 'center',
    widgetOffset: { top: 0, right: 0, bottom: 0, left: 0 },
    widgetWidth: 400,
    dimBackground: false,
    tooltip: {
      enabled: false,
      text: '',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      textColor: '#ffffff',
      textAlign: 'center',
      maxWidth: 200,
    },
  };
}

export function widgetLauncherVisualFromData(
  widget: Record<string, unknown> | null,
  fontFamily: string,
): WidgetLauncherVisual {
  const base = defaultWidgetLauncherVisual(fontFamily);
  if (!widget) return base;
  const button = (widget.button ?? {}) as Record<string, unknown>;
  const icon = (button.icon ?? null) as Record<string, unknown> | null;
  const closeButton = (widget.closeButton ?? {}) as Record<string, unknown>;
  const position = (widget.position ?? {}) as Record<string, unknown>;
  const offset = (position.offset ?? {}) as Record<string, unknown>;
  const widgetOffset = (widget.widgetOffset ?? {}) as Record<string, unknown>;
  const tooltip = (widget.tooltip ?? {}) as Record<string, unknown>;
  const iconUrl = typeof icon?.url === 'string' ? icon.url : '';
  const iconEnabled = icon?.enabled !== false && iconUrl.length > 0;

  return {
    ...base,
    text: typeof button.text === 'string' ? button.text : base.text,
    textColor: typeof button.textColor === 'string' && button.textColor ? button.textColor : base.textColor,
    fontSize: num(button.fontSize, base.fontSize),
    fontWeight: num(button.fontWeight, base.fontWeight),
    fontFamily,
    writingMode: button.writingMode === 'vertical' ? 'vertical' : 'horizontal',
    tcyEnabled: button.tcyEnabled === true,
    backgroundColor:
      typeof button.backgroundColor === 'string' && button.backgroundColor
        ? button.backgroundColor
        : base.backgroundColor,
    borderColor: typeof button.borderColor === 'string' ? button.borderColor : base.borderColor,
    borderWidth: num(button.borderWidth, base.borderWidth),
    width: num(button.width, base.width),
    height: num(button.height, base.height),
    borderRadius: num(button.borderRadius, base.borderRadius),
    paddingY: num(button.paddingY, base.paddingY),
    paddingX: num(button.paddingX, base.paddingX),
    buttonType: button.buttonType === 'image' ? 'image' : 'text',
    imageUrl: button.buttonType === 'image' && typeof button.imageUrl === 'string' ? button.imageUrl : '',
    imageFit: asImageFit(button.imageFit),
    imageMode: button.imageMode === 'image-only' ? 'image-only' : 'styled',
    showCloseButton: button.showCloseButton === true,
    icon: iconEnabled
      ? {
          url: iconUrl,
          size: num(icon?.size, 20),
          color: typeof icon?.color === 'string' && icon.color ? icon.color : '#ffffff',
          colorEnabled: icon?.colorEnabled !== false,
          position: icon?.position === 'after' ? 'after' : 'before',
        }
      : null,
    closeButton: {
      iconColor: typeof closeButton.iconColor === 'string' ? closeButton.iconColor : base.closeButton.iconColor,
      backgroundColor:
        typeof closeButton.backgroundColor === 'string'
          ? closeButton.backgroundColor
          : base.closeButton.backgroundColor,
      opacity: num(closeButton.opacity, base.closeButton.opacity),
      borderColor:
        typeof closeButton.borderColor === 'string' ? closeButton.borderColor : base.closeButton.borderColor,
    },
    positionPreset: typeof position.preset === 'string' && position.preset ? position.preset : base.positionPreset,
    offset: {
      top: num(offset.top, 0),
      right: num(offset.right, 0),
      bottom: num(offset.bottom, 0),
      left: num(offset.left, 0),
    },
    edge: widget.edge === 'window' ? 'window' : 'content',
    widgetPosition: asWidgetPreset(widget.widgetPosition),
    widgetOffset: {
      top: num(widgetOffset.top, 0),
      right: num(widgetOffset.right, 0),
      bottom: num(widgetOffset.bottom, 0),
      left: num(widgetOffset.left, 0),
    },
    widgetWidth: num(widget.widgetWidth, base.widgetWidth),
    dimBackground: widget.dimBackground === true,
    tooltip: {
      enabled: tooltip.enabled === true,
      text: typeof tooltip.text === 'string' ? tooltip.text : '',
      backgroundColor:
        typeof tooltip.backgroundColor === 'string' && tooltip.backgroundColor
          ? tooltip.backgroundColor
          : base.tooltip.backgroundColor,
      textColor:
        typeof tooltip.textColor === 'string' && tooltip.textColor ? tooltip.textColor : base.tooltip.textColor,
      textAlign: tooltip.textAlign === 'left' ? 'left' : 'center',
      maxWidth: num(tooltip.maxWidth, base.tooltip.maxWidth),
    },
  };
}

/** 見た目パネルのドラフトをプレビューに載せる。未変更なら同じ参照を返す。fontFamily はプレビュー側のまま。 */
export function withAppearanceDraft(
  visual: WidgetLauncherVisual,
  draft: AppearanceDraft,
  baseline: AppearanceDraft,
): WidgetLauncherVisual {
  if (appearanceDraftsEqual(draft, baseline)) return visual;
  const iconOn = draft.icon.enabled && draft.icon.url.trim().length > 0;
  return {
    ...visual,
    text: draft.buttonText,
    textColor: draft.textColor,
    fontSize: draft.fontSize,
    fontWeight: draft.fontWeight,
    writingMode: draft.writingMode,
    tcyEnabled: draft.tcyEnabled,
    backgroundColor: draft.backgroundColor,
    borderColor: draft.borderColor,
    borderWidth: draft.borderWidth,
    width: draft.buttonWidth,
    height: draft.buttonHeight,
    borderRadius: draft.borderRadius,
    paddingY: draft.paddingY,
    paddingX: draft.paddingX,
    buttonType: draft.buttonType,
    imageUrl: draft.buttonType === 'image' ? draft.imageUrl : '',
    imageFit: draft.imageFit,
    imageMode: draft.imageMode,
    showCloseButton: draft.showCloseButton,
    icon: iconOn
      ? {
          url: draft.icon.url,
          size: draft.icon.size,
          color: draft.icon.color,
          colorEnabled: draft.icon.colorEnabled,
          position: draft.icon.position,
        }
      : null,
    closeButton: { ...draft.closeButton },
    positionPreset: draft.buttonPosition,
    offset: { ...draft.buttonOffset },
    edge: draft.edge,
    widgetPosition: draft.widgetPosition,
    widgetOffset: { ...draft.widgetOffset },
    widgetWidth: draft.widgetWidth,
    dimBackground: draft.dimBackground,
    tooltip: {
      enabled: draft.tooltip.enabled && draft.tooltip.text.trim().length > 0,
      text: draft.tooltip.text,
      backgroundColor: draft.tooltip.backgroundColor,
      textColor: draft.tooltip.textColor,
      textAlign: draft.tooltip.textAlign,
      maxWidth: draft.tooltip.maxWidth,
    },
  };
}
